import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Clock, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, Users, CalendarDays, MapPin, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
const C = { ink: "#1A1A1A", sub: "#52525B", mute: "#A1A1AA", line: "#E4E4E7", bg: "#F9F8F6", navy: "#121B28", sand: "#F4EBD7", olive: "#8a9771" };
const money = (n, cur = "USD") => new Intl.NumberFormat(cur === "EUR" ? "en-IE" : "en-US", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(n) || 0).replace("US$", "$");
const fmtDate = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d || ""; } };

const Fonts = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Manrope:wght@300;400;500;600;700&display=swap');
  .et-serif{font-family:'Cormorant Garamond',Georgia,serif;}.et-sans{font-family:'Manrope',system-ui,sans-serif;}`}</style>
);

/** Página de pago del comprador de un Curated Trip: cuenta atrás del hold, opciones de pago y vuelta de PayPal. */
export default function PayCurated() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const paypalFlag = sp.get("paypal");
  const orderId = sp.get("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [option, setOption] = useState("");
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [paid, setPaid] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    try {
      const { data: d } = await pub.get(`/curated/booking/${token}`);
      setData(d);
      const first = (d.options || []).find((o) => o.enabled);
      setOption((o) => o || (first ? first.key : ""));
    } catch (e) { setError(e?.response?.data?.detail || "This reservation link is not available."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (paypalFlag !== "return" || !orderId) return;
    let on = true;
    (async () => {
      setCapturing(true);
      try {
        const { data: d } = await pub.post(`/curated/booking/${token}/capture-order/${orderId}`);
        if (!on) return;
        if (d.status === "COMPLETED") { setPaid(d); await load(); }
        else setError("We couldn't confirm your payment. Please try again or contact us.");
      } catch (e) { if (on) setError(e?.response?.data?.detail || "Something went wrong finalising your payment."); }
      finally { if (on) setCapturing(false); }
    })();
    return () => { on = false; };
    // eslint-disable-next-line
  }, [paypalFlag, orderId, token]);

  const remainingMs = data?.hold_expires_at ? new Date(data.hold_expires_at).getTime() - now : 0;
  const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");
  const cur = data?.currency || "USD";
  const isPaid = data?.status === "paid";
  const isSecured = data?.status === "secured";
  const isExpired = !isPaid && !isSecured && (["expired", "cancelled", "refunded"].includes(data?.status) || (data?.status === "hold" && data?.hold_expires_at && remainingSec === 0));
  const options = data?.options || [];
  const selected = useMemo(() => options.find((o) => o.key === option), [options, option]);
  const customNum = Number(custom || 0);
  const amountDue = option === "custom" ? customNum : Number(selected?.amount || 0);
  const canPay = !!option && amountDue > 0 && (option !== "custom" || customNum <= (data?.remaining || 0) + 0.01) && !submitting && !isExpired && !isPaid;

  const pay = async () => {
    if (!canPay) return;
    setSubmitting(true);
    try {
      const body = { option }; if (option === "custom") body.custom_amount = customNum;
      const { data: o } = await pub.post(`/curated/booking/${token}/create-order`, body);
      if (o.approve_url) { window.location.href = o.approve_url; return; }
      setError("Could not start the PayPal checkout. Please try again.");
    } catch (e) { setError(e?.response?.data?.detail || "PayPal could not be reached. Please try again in a moment."); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="et-sans flex min-h-screen items-center justify-center" style={{ backgroundColor: C.bg }}><Fonts /><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.navy }} /></div>;
  if (error && !data) return (
    <div className="et-sans flex min-h-screen flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: C.bg }} data-testid="curated-pay-error">
      <Fonts /><AlertTriangle className="mb-4 h-10 w-10" style={{ color: C.olive }} /><p className="max-w-md" style={{ color: C.ink }}>{error}</p>
    </div>
  );

  const rooms = (data.room_selections || []).reduce((acc, r) => ({ ...acc, [r.room_type]: (acc[r.room_type] || 0) + 1 }), {});
  const roomsLine = Object.entries(rooms).map(([k, n]) => `${n} × ${k === "single" ? "single room" : "shared double"}`).join(" · ");

  return (
    <div className="et-sans min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="curated-pay-page">
      <Fonts />
      <header className="border-b bg-white/90 backdrop-blur" style={{ borderColor: C.line }}>
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <img src="/brand/et-logo-navy.png" alt="Espíritu Travel" className="h-8 w-auto" />
          <Link to={`/curated/${data.public_token}`} className="inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-70" data-testid="curated-pay-back"><ArrowLeft className="h-3.5 w-3.5" /> Back to the trip</Link>
        </div>
      </header>

      <section className="relative h-56 w-full overflow-hidden" style={{ backgroundColor: C.navy }}>
        {data.hero_image && <img src={imgUrl(data.hero_image)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="relative mx-auto flex h-full max-w-3xl flex-col justify-end px-5 pb-6 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70">Curated · Small group</p>
          <h1 className="et-serif text-3xl font-medium sm:text-4xl" data-testid="curated-pay-title">{data.trip_name}</h1>
          <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/85">
            {data.destination && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{data.destination}</span>}
            {data.start_date && <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(data.start_date)} – {fmtDate(data.end_date)}</span>}
            <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{data.seats} {data.seats === 1 ? "seat" : "seats"} · {roomsLine}</span>
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-5 py-10">
        {capturing && <p className="mb-6 flex items-center gap-2 text-sm" style={{ color: C.sub }} data-testid="curated-pay-capturing"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment with PayPal…</p>}
        {paypalFlag === "cancel" && !paid && <p className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="curated-pay-cancelled">Payment cancelled — your seats are still held while the timer runs.</p>}

        {(isPaid || isSecured) ? (
          <div className="border bg-white p-8 text-center" style={{ borderColor: C.line }} data-testid="curated-pay-success">
            <CheckCircle2 className="mx-auto mb-4 h-14 w-14" style={{ color: C.olive }} />
            <h2 className="et-serif text-3xl font-medium">{isPaid ? "You're in — seats confirmed!" : "Your seats are secured"}</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed" style={{ color: C.sub }}>
              {paid ? `We've received your ${money(paid.amount, cur)} payment. ` : ""}
              {isPaid ? "Your reservation is fully paid. We'll email you the next steps before departure."
                : `Your deposit confirms your ${data.seats} ${data.seats === 1 ? "seat" : "seats"}. The remaining ${money(data.remaining, cur)} is due ${data.full_payment_days} days before departure — you can pay it below whenever you like.`}
            </p>
            <div className="mx-auto mt-6 grid max-w-sm grid-cols-3 gap-3 text-left text-sm">
              <div><p className="text-[10px] uppercase tracking-wider" style={{ color: C.mute }}>Total</p><p className="font-semibold">{money(data.total, cur)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider" style={{ color: C.mute }}>Paid</p><p className="font-semibold text-emerald-700" data-testid="curated-pay-paid">{money(data.paid, cur)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider" style={{ color: C.mute }}>Remaining</p><p className="font-semibold" data-testid="curated-pay-remaining">{money(data.remaining, cur)}</p></div>
            </div>
          </div>
        ) : isExpired ? (
          <div className="border bg-white p-8 text-center" style={{ borderColor: C.line }} data-testid="curated-pay-expired">
            <Clock className="mx-auto mb-4 h-12 w-12" style={{ color: C.mute }} />
            <h2 className="et-serif text-3xl font-medium">Your hold has expired</h2>
            <p className="mx-auto mt-3 max-w-md text-sm" style={{ color: C.sub }}>The {data.hold_minutes}-minute window closed before the payment went through, so the seats were released. If there's still availability you can reserve again.</p>
            <Link to={`/curated/${data.public_token}`} className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: C.navy }} data-testid="curated-pay-retry">Back to the trip <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[1fr_300px]">
            <div className="border bg-white p-6" style={{ borderColor: C.line }}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: C.olive }}>Choose how to pay</p>
              <div className="mt-4 space-y-2" data-testid="curated-pay-options">
                {options.filter((o) => o.enabled).map((o) => (
                  <label key={o.key} className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition" style={{ borderColor: option === o.key ? C.navy : C.line, backgroundColor: option === o.key ? "rgba(18,27,40,0.03)" : "white" }} data-testid={`curated-pay-option-${o.key}`}>
                    <input type="radio" name="opt" checked={option === o.key} onChange={() => setOption(o.key)} className="mt-1" style={{ accentColor: C.navy }} />
                    <span className="flex-1">
                      <span className="flex items-baseline justify-between gap-3"><span className="text-sm font-semibold">{o.label}</span>{o.amount != null && <span className="text-sm font-semibold">{money(o.amount, cur)}</span>}</span>
                      <span className="block text-xs" style={{ color: C.sub }}>{o.description}</span>
                    </span>
                  </label>
                ))}
                {option === "custom" && <Input type="number" min={1} max={data.remaining} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={`Amount in ${cur}`} data-testid="curated-pay-custom" />}
              </div>
              {(data.days_until ?? 999) <= data.full_payment_days && <p className="mt-3 text-xs" style={{ color: C.sub }}>Departure is within {data.full_payment_days} days, so the full amount is due to confirm your seats.</p>}
              {error && <p className="mt-3 text-sm text-red-700" data-testid="curated-pay-error-inline">{error}</p>}
              <button onClick={pay} disabled={!canPay} data-testid="curated-pay-submit"
                className="mt-6 flex w-full items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: C.navy }}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Pay {amountDue > 0 ? money(amountDue, cur) : ""} with PayPal
              </button>
              <p className="mt-3 text-center text-[11px]" style={{ color: C.mute }}>Secure payment · powered by PayPal · you'll be redirected and brought back here.</p>
            </div>
            <aside className="space-y-4">
              <div className="border p-5" style={{ borderColor: C.line, backgroundColor: C.sand }} data-testid="curated-pay-timer">
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.sub }}><Clock className="h-3.5 w-3.5" /> Seats held for</p>
                <p className="et-serif mt-1 text-5xl font-medium tabular-nums" data-testid="curated-pay-countdown">{mm}:{ss}</p>
                <p className="mt-1 text-xs" style={{ color: C.sub }}>Complete the payment before the timer ends to keep your seats.</p>
              </div>
              <div className="border bg-white p-5 text-sm" style={{ borderColor: C.line }}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.mute }}>Your reservation</p>
                <p className="mt-2 font-semibold">{data.buyer_name}</p><p className="text-xs" style={{ color: C.sub }}>{data.buyer_email}</p>
                <div className="mt-3 flex items-baseline justify-between border-t pt-3" style={{ borderColor: C.line }}><span style={{ color: C.sub }}>Total</span><span className="et-serif text-2xl" data-testid="curated-pay-total">{money(data.total, cur)}</span></div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
